import { clsx } from 'clsx'
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { X } from './icons'

type Tone = 'info' | 'success' | 'error'
type Toast = { id: number; message: string; tone: Tone }

type Api = { toast: (message: string, tone?: Tone) => void }
const Ctx = createContext<Api | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([])
  const seq = useRef(0)
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), [])
  const toast = useCallback(
    (message: string, tone: Tone = 'info') => {
      const id = ++seq.current
      setItems((xs) => [...xs, { id, message, tone }])
      window.setTimeout(() => dismiss(id), tone === 'error' ? 8000 : 4000)
    },
    [dismiss],
  )
  const api = useMemo(() => ({ toast }), [toast])
  return (
    <Ctx.Provider value={api}>
      {children}
      <div aria-live="polite" aria-atomic="false" className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4">
        {items.map((t) => (
          <div
            key={t.id}
            role={t.tone === 'error' ? 'alert' : 'status'}
            className={clsx(
              'pointer-events-auto flex max-w-md items-center gap-3 px-4 py-2.5 text-small shadow-[0_8px_24px_rgb(0_0_0/0.18)]',
              t.tone === 'error' ? 'bg-danger text-paper' : 'bg-eerie text-paper',
            )}
          >
            <span>{t.message}</span>
            <button type="button" onClick={() => dismiss(t.id)} aria-label="Dismiss" className="ml-auto inline-flex h-6 w-6 items-center justify-center opacity-70 hover:opacity-100">
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  )
}

export function useToast(): Api {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}
